// src/core/debugParams.ts
// 调试参数门面（EP-009-S4）—— playtest / QA 用的 URL 查询参数统一入口。
//
// ════════════════════════════════════════════════════════════════════════════
// 双门控契约（为什么 DebugPanel 必须在生产构建里绝对消失）
// ════════════════════════════════════════════════════════════════════════════
//
// 1. DEV 门：`import.meta.env.DEV` 为 true。Vite 生产构建会把 DEV 静态替换为
//    `false`，于是 `isDebugMode()` 立即短路为 false、`getTimeScale()` 恒返回 1、
//    `getFixedSeed()` 恒返回 null。这是编译期保证，无法在运行时绕过。
//
// 2. URL 门：`?debug=1`（默认）。即便有人拿到一份 DEV 预发布构建，只要 URL
//    没有 `?debug=1`，调试面板仍然隐藏。
//
// 两个门任一关闭（DEV 关闭 OR URL 无 debug=1）→ `isDebugMode()` 立即返回 false，
// `getTimeScale()` / `getFixedSeed()` 强制走默认值——URL 里的 `?timeScale=` /
// `?seed=` 完全被忽略。即便生产构建意外保留 URL 参数，没有 `?debug=1` 也读不到。
//
// ⚠️ 严禁把 `getTimeScale()` / `getFixedSeed()` 接到正式玩家 UI 上：
//   它们只供 playtest 场景。游戏循环里要走 `useGameStore` 的 `settings.timeScale`，
//   不能让 URL 参数污染游戏逻辑——这是 ADR 层面"调试参数绝不进 domain"的红线。
//
// 合法值约定：
//   ?timeScale=  → 只接受 1 / 0.1 / 0.01；其他降级为 1（保守默认）
//   ?seed=       → 任意有限整数；非整数 / NaN / Infinity → null（不注入种子）
// ════════════════════════════════════════════════════════════════════════════

// 合法 timeScale 集合（写死成 tuple 便于 `Array.some` 直接用，无需 `as` 类型断言）
const ALLOWED_TIME_SCALES: readonly number[] = [1, 0.1, 0.01];

/** URL 参数解析后的结构化结果（纯函数，便于单测）。 */
export interface ParsedDebugSearch {
  /** `?debug=1` 是否出现 */
  debug: boolean;
  /** timeScale（URL 缺失或非法时降级为 1） */
  timeScale: number;
  /** 固定种子（URL 缺失或非法时为 null） */
  fixedSeed: number | null;
}

/**
 * 把一段 `window.location.search` 风格的 query 字符串解析为结构化结果。
 * 纯函数：不读 `import.meta.env.DEV`、不读 `window`——只看入参 `search`。
 * 用于：① 单元测试 ② 调试面板自身（避免重复实现解析逻辑）。
 */
export function parseDebugSearch(search: string): ParsedDebugSearch {
  // URLSearchParams 接受带 `?` 前缀或不带前缀——两种都安全。
  const params = new URLSearchParams(search);
  const debug = params.get('debug') === '1';

  const timeScaleRaw = params.get('timeScale');
  let timeScale = 1;
  if (timeScaleRaw !== null) {
    const n = Number(timeScaleRaw);
    if (ALLOWED_TIME_SCALES.some((v) => v === n)) {
      timeScale = n;
    }
    // 否则保持 1（保守默认，符合任务规约"非法值降级到 1"）
  }

  const seedRaw = params.get('seed');
  let fixedSeed: number | null = null;
  if (seedRaw !== null) {
    const n = Number(seedRaw);
    // Number('1.5') = 1.5，Number.isInteger 会卡掉；
    // Number('abc') = NaN，Number.isFinite 会卡掉；
    // Number('Infinity') = Infinity，同上。
    if (Number.isFinite(n) && Number.isInteger(n)) {
      fixedSeed = n;
    }
    // 否则 null
  }

  return { debug, timeScale, fixedSeed };
}

/**
 * 读取当前 `window.location.search` 并调用 `parseDebugSearch`。
 * 无 `window`（SSR / node）一律返回空 query 结果。
 */
function readCurrentSearch(): ParsedDebugSearch {
  if (typeof window === 'undefined') {
    return { debug: false, timeScale: 1, fixedSeed: null };
  }
  return parseDebugSearch(window.location.search);
}

/**
 * 是否处于调试模式：DEV 构建 **且** URL 含 `?debug=1`。
 * 两个条件同时成立才返回 true；任一不满足立刻 false。
 *
 * 生产构建 `import.meta.env.DEV` 静态替换为 `false`，整个函数短路为 false，
 * 调用方拿到的就是"不在调试模式"——`getTimeScale()` / `getFixedSeed()` 因此
 * 立即走默认值，URL 里的参数被完全忽略。
 */
export function isDebugMode(): boolean {
  if (!import.meta.env.DEV) return false;
  return readCurrentSearch().debug;
}

/**
 * 获取调试用 timeScale：仅调试模式下生效，否则强制返回 1。
 * 调用方应在游戏循环中与 `save.settings.timeScale` 取并集——本函数只是
 * playtest 场景的覆盖层，不替代玩家设置。
 */
export function getTimeScale(): number {
  if (!isDebugMode()) return 1;
  return readCurrentSearch().timeScale;
}

/**
 * 获取调试用固定种子：仅调试模式下生效，否则强制返回 null。
 * 返回 null 表示"无固定种子，使用默认 rng 路径"——store / engine 侧据此
 * 决定要不要用 `rng(seed)` 而不是 `randomRng()`。
 */
export function getFixedSeed(): number | null {
  if (!isDebugMode()) return null;
  return readCurrentSearch().fixedSeed;
}
